import axios from 'axios'

export type NotificationType = 
  | 'reservation_confirmed'
  | 'payment_received'
  | 'new_basket_nearby'
  | 'review_requested'
  | 'payout_processed'
  | 'favorite_product_available'
  | 'system_alert'

export interface Notification {
  notification_id: string
  user_id: string
  type: NotificationType
  title: string
  message: string
  data: any
  is_read: boolean
  created_at: string
  expires_at: string
  user_first_name: string | null
  user_last_name: string | null
}

export interface CreateNotificationDto {
  user_id: string
  type: NotificationType
  title: string
  message: string
  data?: any
  expires_in_hours?: number
}

export interface UpdateNotificationDto {
  type?: NotificationType
  title?: string
  message?: string
  data?: any
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
  unread_count?: number
}

export interface NotificationFilters {
  user_id?: string
  type?: string
  is_read?: boolean
  unread_only?: boolean
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const notificationApi = axios.create({
  baseURL: `${API_BASE_URL}/notifications`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
notificationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
notificationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const notificationService = {
  // Get all notifications with optional filters
  async getAll(filters: NotificationFilters = {}): Promise<PaginatedResponse<Notification>> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.type) params.append('type', filters.type)
    if (filters.is_read !== undefined) params.append('is_read', filters.is_read.toString())
    if (filters.unread_only !== undefined) params.append('unread_only', filters.unread_only.toString())

    const response = await notificationApi.get('', { params })
    return response.data
  },

  // Get notification by ID
  async getById(id: string): Promise<{ success: boolean; data: Notification }> {
    const response = await notificationApi.get(`/${id}`)
    return response.data
  },

  // Create new notification
  async create(notificationData: CreateNotificationDto): Promise<{ success: boolean; data: Notification; message: string }> {
    const response = await notificationApi.post('', notificationData)
    return response.data
  },

  // Create multiple notifications
  async createBulk(notifications: CreateNotificationDto[]): Promise<{ success: boolean; data: Notification[]; message: string }> {
    const response = await notificationApi.post('/bulk', { notifications })
    return response.data
  },

  // Update notification
  async update(id: string, notificationData: UpdateNotificationDto): Promise<{ success: boolean; data: Notification; message: string }> {
    const response = await notificationApi.put(`/${id}`, notificationData)
    return response.data
  },

  // Delete notification
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await notificationApi.delete(`/${id}`)
    return response.data
  },

  // Mark notification as read
  async markAsRead(id: string): Promise<{ success: boolean; data: Notification; message: string }> {
    const response = await notificationApi.patch(`/${id}/read`)
    return response.data
  },

  // Mark all user notifications as read
  async markAllAsRead(userId: string): Promise<{ success: boolean; message: string; updated_count: number }> {
    const response = await notificationApi.patch('/read-all', { user_id: userId })
    return response.data
  },

  // Get notification statistics
  async getStatistics(): Promise<{
    total: number
    unread: number
    byType: Record<string, number>
    recentActivity: { date: string; count: number }[]
  }> {
    const allNotifications = await this.getAll()
    const notifications = allNotifications.data
    
    const byType: Record<string, number> = {}
    const recentActivity: { date: string; count: number }[] = []
    
    // Group by type
    notifications.forEach(notification => {
      byType[notification.type] = (byType[notification.type] || 0) + 1
    })
    
    // Group by date (last 7 days)
    const today = new Date()
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      const dateStr = date.toISOString().split('T')[0]
      
      const count = notifications.filter(n => {
        const notificationDate = new Date(n.created_at).toISOString().split('T')[0]
        return notificationDate === dateStr
      }).length
      
      recentActivity.push({ date: dateStr, count })
    }
    
    const stats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.is_read).length,
      byType,
      recentActivity
    }
    
    return stats
  }
}

export default notificationService
